import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, CornerDownLeft, Sparkles, Eye, ShieldCheck, Clock,
             UserRound, FolderOpen, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { usePermissions } from "@/lib/permissions";
import { NAV_ITEMS } from "@/constants/navItems";
import { QUICK_ACTIONS, KIND_LABEL } from "@/constants/quickActions";
import { useKeyboardShortcut, Kbd } from "@/components/altiora/useKeyboardShortcut";
import { modalOverlay, modalPanel, useSafeMotion } from "@/components/altiora/motion";
import { Badge, NAVY, CHAMPAGNE, IVORY, SLATE } from "@/components/altiora/PremiumUI";

const KIND_ICON = { create: Sparkles, view: Eye, admin: ShieldCheck };

/**
 * ALTIORA · Command Palette (⌘K / Ctrl+K)
 *
 * Global searchable menu — style Linear / Vercel / Notion.
 *   · Opens on ⌘K / Ctrl+K anywhere in the app
 *   · ESC closes
 *   · ↑/↓ navigate results, Enter opens
 *   · Filters against `NAV_ITEMS` respecting the current user's permissions
 *   · Tolerant substring search (case-insensitive, diacritic-tolerant)
 */
export function CommandPalette() {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState("");
  const [cursor, setCursor] = useState(0);
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { permissions, roles, impersonatingAs } = usePermissions();

  // ── Recent routes tracking (localStorage) ──────────────────────
  const [recentPaths, setRecentPaths] = useState(() => {
    try { return JSON.parse(localStorage.getItem("altiora_cp_recent") || "[]"); }
    catch { return []; }
  });
  useEffect(() => {
    if (!location.pathname.startsWith("/app")) return;
    setRecentPaths((prev) => {
      const next = [location.pathname, ...prev.filter((p) => p !== location.pathname)].slice(0, 5);
      try { localStorage.setItem("altiora_cp_recent", JSON.stringify(next)); } catch {}
      return next;
    });
  }, [location.pathname]);

  const overlay = useSafeMotion(modalOverlay);
  const panel   = useSafeMotion(modalPanel);

  // ── Live search across user's own data (opps/dossiers/alerts) ──
  const [liveHits, setLiveHits] = useState([]);
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) { setLiveHits([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}&limit=4`);
        if (cancelled) return;
        const hits = [
          ...(data.opportunities || []).map(x => ({ ...x, _icon: UserRound })),
          ...(data.dossiers      || []).map(x => ({ ...x, _icon: FolderOpen })),
          ...(data.alerts        || []).map(x => ({ ...x, _icon: BellRing  })),
        ];
        setLiveHits(hits);
      } catch { if (!cancelled) setLiveHits([]); }
    }, 220);   // debounce
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open]);

  // ── Open on ⌘K / Ctrl+K ────────────────────────────────────────
  useKeyboardShortcut("k", () => setOpen(o => !o), { meta: true });
  useKeyboardShortcut("Escape", () => setOpen(false), { enabled: open, allowInInput: true });

  // ── Filter by permissions (mirrors Sidebar RBAC) ───────────────
  const permsSet = useMemo(() => new Set(permissions), [permissions]);
  const rolesSet = useMemo(() => new Set(roles), [roles]);
  const isAuthorized = (item) => {
    if (!item.perm) return true;
    if (permsSet.has(item.perm)) return true;
    if (!impersonatingAs && (rolesSet.has("founder") || rolesSet.has("super_admin"))) return true;
    return false;
  };
  const authorizedRoutes  = useMemo(() => NAV_ITEMS.filter(isAuthorized),
                                              [permsSet, rolesSet, impersonatingAs]);
  const authorizedActions = useMemo(() => QUICK_ACTIONS.filter(isAuthorized),
                                              [permsSet, rolesSet, impersonatingAs]);

  // ── Fuzzy search on labels (case + diacritic-insensitive) ──────
  const results = useMemo(() => {
    const norm = (s) => (s || "").toString()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const q = norm(query.trim());
    const routesTagged  = authorizedRoutes.map(item => ({
      type: "route", item, label: t(item.label),
    }));
    const actionsTagged = authorizedActions.map(item => ({
      type: "action", item, label: item.label,
    }));
    // Recent section — only when no query
    const recentEntries = q ? [] : recentPaths
      .map((p) => authorizedRoutes.find((r) => r.to === p))
      .filter(Boolean)
      .map((item) => ({ type: "recent", item, label: t(item.label) }));

    // Live-search hits (opportunities / dossiers / alerts) — only when q non-empty
    const liveTagged = (q && liveHits.length)
      ? liveHits.map(h => ({ type: "live", item: h, label: h.title }))
      : [];

    const all = [...liveTagged, ...recentEntries, ...actionsTagged, ...routesTagged];
    if (!q) return all.slice(0, 14);
    return all.filter(({ label }) => norm(label).includes(q)).slice(0, 14);
  }, [query, authorizedRoutes, authorizedActions, recentPaths, liveHits, t]);

  useEffect(() => { setCursor(0); }, [query]);
  useEffect(() => { if (open) { setQuery(""); setCursor(0); } }, [open]);

  const runItem = (entry) => {
    if (!entry) return;
    if (entry.type === "action") {
      entry.item.run(nav);
    } else if (entry.type === "live") {
      nav(entry.item.url);
    } else {
      nav(entry.item.to);
    }
    setOpen(false);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === "Enter")  { e.preventDefault(); runItem(results[cursor]); }
  };

  return (
    <>
      {/* Invisible trigger — palette is only opened via ⌘K or public trigger button */}
      <AnimatePresence>
        {open && (
          <motion.div key="cp-ov" {...overlay}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] flex items-start justify-center pt-[12vh] px-4"
            style={{ background: "rgba(6,21,40,0.55)", backdropFilter: "blur(6px)" }}
            data-testid="cp-overlay">
            <motion.div key="cp-p" {...panel} onClick={e => e.stopPropagation()}
              role="dialog" aria-modal="true" aria-label="Palette de commandes"
              data-testid="cp-panel"
              className="w-full max-w-[560px] rounded-2xl bg-white shadow-2xl overflow-hidden"
              style={{ border: `1px solid ${CHAMPAGNE}44` }}>
              <div className="flex items-center gap-2 px-4 py-3 border-b"
                      style={{ borderColor: `${CHAMPAGNE}22`, background: IVORY }}>
                <Search size={16} aria-hidden="true" style={{ color: CHAMPAGNE }} />
                <input
                  autoFocus
                  data-testid="cp-input"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Rechercher une page, un module, une action…"
                  aria-label="Rechercher une page, un module ou une action"
                  className="flex-1 bg-transparent outline-none text-[15px]"
                  style={{ color: NAVY }} />
                <div className="hidden md:flex items-center gap-1 text-[10px]" style={{ color: SLATE }}>
                  <Kbd>Esc</Kbd>
                </div>
              </div>
              <div className="max-h-[50vh] overflow-y-auto py-1" data-testid="cp-results">
                {results.length === 0 ? (
                  <div className="px-5 py-8 text-center text-[13px]" style={{ color: SLATE }}>
                    Aucun résultat.
                  </div>
                ) : (
                  results.map((entry, i) => {
                    const isActive = i === cursor;
                    if (entry.type === "action") {
                      const A = entry.item;
                      const KIcon = KIND_ICON[A.kind] || Sparkles;
                      return (
                        <button
                          key={`a-${A.id}`}
                          onMouseEnter={() => setCursor(i)}
                          onClick={() => runItem(entry)}
                          data-testid={`cp-action-${A.id}`}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px]"
                          style={{ background: isActive ? `${CHAMPAGNE}18` : "transparent", color: NAVY }}>
                          <KIcon size={15} aria-hidden="true"
                                    style={{ color: isActive ? CHAMPAGNE : SLATE }} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{A.label}</div>
                            {A.hint && (
                              <div className="text-[11px] truncate" style={{ color: SLATE }}>
                                {A.hint}
                              </div>
                            )}
                          </div>
                          <Badge variant={A.kind === "create" ? "gold" : A.kind === "admin" ? "red" : "navy"}
                                   size="sm">
                            {KIND_LABEL[A.kind]}
                          </Badge>
                          {isActive && <CornerDownLeft size={12} style={{ color: CHAMPAGNE }} />}
                        </button>
                      );
                    }
                    if (entry.type === "live") {
                      const H = entry.item;
                      const HIcon = H._icon || UserRound;
                      const kindMap = { opportunity: "gold", dossier: "navy", alert: "red" };
                      return (
                        <button
                          key={`live-${H.kind}-${H.id}`}
                          onMouseEnter={() => setCursor(i)}
                          onClick={() => runItem(entry)}
                          data-testid={`cp-live-${H.kind}-${H.id}`}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px]"
                          style={{ background: isActive ? `${CHAMPAGNE}18` : "transparent", color: NAVY }}>
                          <HIcon size={15} aria-hidden="true"
                                     style={{ color: isActive ? CHAMPAGNE : SLATE }} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{H.title}</div>
                            {H.hint && (
                              <div className="text-[11px] truncate" style={{ color: SLATE }}>
                                {H.hint}
                              </div>
                            )}
                          </div>
                          <Badge variant={kindMap[H.kind] || "neutral"} size="sm">
                            {H.kind === "opportunity" ? "Contact"
                              : H.kind === "dossier"  ? "Dossier"
                              : "Alerte"}
                          </Badge>
                          {isActive && <CornerDownLeft size={12} style={{ color: CHAMPAGNE }} />}
                        </button>
                      );
                    }
                    const R = entry.item;
                    const Icon = R.icon;
                    const isRecent = entry.type === "recent";
                    return (
                      <button
                        key={`${entry.type}-${R.key}`}
                        onMouseEnter={() => setCursor(i)}
                        onClick={() => runItem(entry)}
                        data-testid={`cp-item-${R.key}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px]"
                        style={{ background: isActive ? `${CHAMPAGNE}18` : "transparent", color: NAVY }}>
                        <Icon size={15} aria-hidden="true"
                                  style={{ color: isActive ? CHAMPAGNE : SLATE }} />
                        <span className="flex-1 truncate">{t(R.label)}</span>
                        {isRecent ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest"
                                   style={{ color: CHAMPAGNE }}>
                            <Clock size={9} /> Récent
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-widest"
                                  style={{ color: SLATE }}>
                            {R.group}
                          </span>
                        )}
                        {isActive && <CornerDownLeft size={12} style={{ color: CHAMPAGNE }} />}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="flex items-center justify-between gap-2 px-4 py-2 border-t text-[10px]"
                      style={{ borderColor: `${CHAMPAGNE}22`, color: SLATE, background: IVORY }}>
                <div className="flex items-center gap-2">
                  <Kbd>↑</Kbd><Kbd>↓</Kbd><span>Naviguer</span>
                  <Kbd><CornerDownLeft size={9} /></Kbd><span>Ouvrir</span>
                </div>
                <div className="flex items-center gap-1">
                  <Kbd>⌘</Kbd><Kbd>K</Kbd>
                  <span className="ml-1">Ouvrir/fermer</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Small visible trigger — put in the TopBar or a mobile button. */
export function CommandPaletteTrigger({ testid = "cp-trigger", className = "" }) {
  return (
    <button
      onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
      data-testid={testid}
      aria-label="Ouvrir la palette de commandes (Cmd+K)"
      title="Palette de commandes (⌘K)"
      className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1 transition hover:opacity-90 ${className}`}
      style={{ border: `1px solid ${CHAMPAGNE}44`, color: `${IVORY}CC` }}>
      <Search size={11} aria-hidden="true" />
      <span className="text-[10px] font-mono uppercase tracking-widest">Rechercher</span>
      <span className="hidden md:inline"><Kbd>⌘K</Kbd></span>
    </button>
  );
}
